import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Trophy, Medal, Award, Crown, Star, Flame, Zap, Target, 
  MessageSquare, Calendar, Users, TrendingUp, Gift, Sparkles,
  UserPlus, Clock, Moon, Sun, Shield, Rocket, CheckCircle
} from 'lucide-react';

const iconMap = {
  UserPlus: UserPlus,
  Calendar: Calendar,
  Presentation: Star,
  Users: Users,
  MessageSquare: MessageSquare,
  Zap: Zap,
  Clock: Clock,
  TrendingUp: TrendingUp,
  CalendarCheck: Calendar,
  Star: Star,
  Award: Award,
  Trophy: Trophy,
  Flame: Flame,
  Fire: Flame,
  Rocket: Rocket,
  Sunrise: Sun,
  Moon: Moon,
  Shield: Shield,
  Crown: Crown
};

const periodOptions = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This Week' },
  { value: 'monthly', label: 'This Month' }
];

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="h-6 w-6 text-yellow-500" />;
  if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" />;
  if (rank === 3) return <Medal className="h-6 w-6 text-amber-600" />;
  return <span className="text-lg font-bold text-gray-500">#{rank}</span>;
};

const getRankBgColor = (rank) => {
  if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300';
  if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300';
  if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300';
  return 'bg-white border-gray-200';
};

// Moved outside component to avoid re-creation on each render
const BadgeCard = ({ badge, size = 'normal' }) => {
  const IconComponent = iconMap[badge.icon] || Award;
  const isSmall = size === 'small';
  
  return (
    <div 
      className={`flex items-center gap-2 p-2 rounded-lg border ${isSmall ? 'text-xs' : ''}`}
      style={{ backgroundColor: `${badge.color}15`, borderColor: `${badge.color}40` }}
    >
      <div 
        className={`${isSmall ? 'w-6 h-6' : 'w-10 h-10'} rounded-full flex items-center justify-center`}
        style={{ backgroundColor: badge.color }}
      >
        <IconComponent className={`${isSmall ? 'h-3 w-3' : 'h-5 w-5'} text-white`} />
      </div>
      <div>
        <p className={`font-medium ${isSmall ? 'text-xs' : 'text-sm'}`}>{badge.name}</p>
        {!isSmall && <p className="text-xs text-gray-500">{badge.description}</p>}
      </div>
      <Badge className="ml-auto" style={{ backgroundColor: badge.color }}>
        +{badge.points}
      </Badge>
    </div>
  );
};

export const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [badgeDefinitions, setBadgeDefinitions] = useState({});
  const [userStats, setUserStats] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaderboardRes, badgesRes, definitionsRes, statsRes] = await Promise.all([
        api.get(`/leaderboard?period=${period}`),
        api.get('/badges'),
        api.get('/badges/definitions'),
        api.get('/stats/summary')
      ]);
      
      setLeaderboard(leaderboardRes.data.leaderboard);
      setUserBadges(badgesRes.data);
      setBadgeDefinitions(definitionsRes.data);
      setUserStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const checkForNewBadges = async () => {
    try {
      const response = await api.post('/badges/check');
      if (response.data.new_badges.length > 0) {
        response.data.new_badges.forEach(badge => {
          toast.success(`🏆 New Badge Earned: ${badge.name}!`, {
            description: `${badge.description} (+${badge.points} points)`,
            duration: 5000
          });
        });
        fetchData();
      } else {
        toast.info('No new badges earned yet. Keep working!');
      }
    } catch (error) {
      toast.error('Failed to check badges');
    }
  };

  const currentUserRank = leaderboard.find(e => e.user_id === user?.id);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#333333] flex items-center gap-2">
            <Trophy className="h-8 w-8 text-[#E31837]" />
            Leaderboard & Badges
          </h1>
          <p className="text-gray-600">Compete with your team and earn achievements</p>
        </div>
        
        <Button
          onClick={checkForNewBadges}
          className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
          data-testid="check-badges-button"
        >
          <Sparkles className="h-4 w-4" />
          Check for New Badges
        </Button>
      </div>

      {/* User Stats Summary */}
      {userStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4 text-center">
            <Trophy className="h-6 w-6 text-[#E31837] mx-auto mb-2" />
            <p className="text-2xl font-bold text-[#333333]">#{userStats.rank || '-'}</p>
            <p className="text-xs text-gray-500">Your Rank</p>
          </Card>
          <Card className="p-4 text-center">
            <Award className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-600">{userStats.badges.count}</p>
            <p className="text-xs text-gray-500">Badges Earned</p>
          </Card>
          <Card className="p-4 text-center">
            <Star className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-600">{userStats.badges.points}</p>
            <p className="text-xs text-gray-500">Total Points</p>
          </Card>
          <Card className="p-4 text-center">
            <Flame className="h-6 w-6 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-600">{userStats.streak}</p>
            <p className="text-xs text-gray-500">Day Streak</p>
          </Card>
          <Card className="p-4 text-center">
            <MessageSquare className="h-6 w-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{userStats.all_time.messages}</p>
            <p className="text-xs text-gray-500">Total Messages</p>
          </Card>
          <Card className="p-4 text-center">
            <Users className="h-6 w-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{userStats.all_time.recruits}</p>
            <p className="text-xs text-gray-500">Total Recruits</p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leaderboard */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Trophy className="h-5 w-5 text-[#E31837]" />
                Team Rankings
              </h2>
              
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList>
                  {periodOptions.map(opt => (
                    <TabsTrigger key={opt.value} value={opt.value}>
                      {opt.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No activity data yet. Start prospecting to appear on the leaderboard!
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry, idx) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  
                  return (
                    <div
                      key={entry.user_id}
                      data-testid={`leaderboard-entry-${entry.user_id}`}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                        getRankBgColor(entry.rank)
                      } ${isCurrentUser ? 'ring-2 ring-[#E31837]' : ''}`}
                    >
                      {/* Rank */}
                      <div className="w-12 flex justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#333333]">
                            {entry.name}
                            {isCurrentUser && <span className="text-[#E31837] text-sm ml-1">(You)</span>}
                          </p>
                          <Badge variant="outline" className="text-xs">
                            {entry.role.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        {/* Stats Row */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {entry.stats.messages} msgs
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {entry.stats.appointments} appts
                          </span>
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3" />
                            {entry.stats.presentations} pres
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {entry.stats.recruits} recruits
                          </span>
                        </div>
                      </div>
                      
                      {/* Score & Badges */}
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#E31837]">{entry.score.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">
                          {entry.badges} badges • {entry.points} pts
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Badges Section */}
        <div>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Award className="h-5 w-5 text-purple-600" />
                Your Badges
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBadgesModal(true)}
              >
                View All
              </Button>
            </div>

            {userBadges.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Gift className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No badges earned yet</p>
                <p className="text-sm">Complete activities to earn badges!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {userBadges.slice(0, 5).map((badge, idx) => (
                  <BadgeCard key={idx} badge={badge} size="small" />
                ))}
                {userBadges.length > 5 && (
                  <p className="text-sm text-center text-gray-500 pt-2">
                    +{userBadges.length - 5} more badges
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Badge Progress */}
          <Card className="p-6 mt-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Target className="h-4 w-4 text-[#E31837]" />
              Next Badges to Earn
            </h3>
            
            <div className="space-y-4">
              {/* Daily 50 messages */}
              {!userBadges.some(b => b.badge_type === 'daily_50_messages') && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Message Master (50/day)</span>
                    <span className="text-gray-500">
                      {userStats?.all_time.messages || 0}/50 today
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(((userStats?.all_time.messages || 0) / 50) * 100, 100)} 
                    className="h-2"
                  />
                </div>
              )}
              
              {/* Streak badges */}
              {!userBadges.some(b => b.badge_type === 'streak_7_days') && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Week Streak (7 days)</span>
                    <span className="text-gray-500">
                      {userStats?.streak || 0}/7 days
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(((userStats?.streak || 0) / 7) * 100, 100)} 
                    className="h-2"
                  />
                </div>
              )}
              
              {/* S2D badge */}
              {!userBadges.some(b => b.badge_type === 'monthly_s2d') && (
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Sprint Champion (S2D)</span>
                    <span className="text-gray-500">In Progress</span>
                  </div>
                  <Progress value={33} className="h-2" />
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* All Badges Modal */}
      <Dialog open={showBadgesModal} onOpenChange={setShowBadgesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="all-badges-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              All Badges ({Object.keys(badgeDefinitions).length})
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Earned Badges */}
            <div>
              <h3 className="font-semibold mb-3 text-green-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Earned ({userBadges.length})
              </h3>
              {userBadges.length === 0 ? (
                <p className="text-gray-500 text-sm">No badges earned yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {userBadges.map((badge, idx) => (
                    <BadgeCard key={idx} badge={badge} />
                  ))}
                </div>
              )}
            </div>
            
            {/* Unearned Badges */}
            <div>
              <h3 className="font-semibold mb-3 text-gray-500 flex items-center gap-2">
                <Target className="h-4 w-4" />
                Available to Earn ({Object.keys(badgeDefinitions).length - userBadges.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(badgeDefinitions)
                  .filter(([key]) => !userBadges.some(b => b.badge_type === key))
                  .map(([key, badge]) => (
                    <div 
                      key={key}
                      className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-gray-50 opacity-60"
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-300"
                      >
                        <Award className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{badge.name}</p>
                        <p className="text-xs text-gray-500">{badge.description}</p>
                      </div>
                      <Badge variant="outline" className="ml-auto">
                        +{badge.points}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
